import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
    changeUserDetail, getAgencies, getAllowedRegion, getPropertyManagerAccess,
    getUserbyUsername
} from "../service/UserService";
import { getAllProperties } from "../service/PropertyService";
import {
    deleteAgencyDefault, getAgencyDefaults, getBusinessesAndAccounts,
    getProfileList, setAgencyDefault
} from "../service/MyobService";
import { USER_ROLES } from "../constants/userRoles";
import { 
    Button, Form, Card, Container, Row, Col, 
    Alert, Spinner, InputGroup, Modal
} from "react-bootstrap";
import { 
    FaUser, FaLock, FaIdCard, FaUserTag, 
    FaPhone, FaEnvelope, FaUserCog, FaMapMarkerAlt, 
    FaEye, FaEyeSlash, FaArrowLeft, FaSave, FaTrash
} from "react-icons/fa";
import AgencyAutocomplete from "./AgencyAutocomplete";
import {buildUserUpdateRequest} from "./userUpdateRequest";
import {getWorkerProfileCatalogs} from "../service/WorkerSettingsService";
import WorkerProfileEditor from "./workerProfile/WorkerProfileEditor";
import {createDefaultWorkerProfile, normalizeWorkerProfile} from "./workerProfile/workerProfileModel";

const EMPTY_MYOB_MAPPING = {
    myob_profile_id: '',
    myob_business_id: '',
    myob_contact_id: '',
};

async function requireSuccess(response, fallback) {
    if (response.ok) return response;
    const body = await response.json().catch(() => null);
    throw new Error(body?.message || fallback);
}

function normalizeMyobMapping(mapping) {
    return {
        myob_profile_id: mapping?.myob_profile_id || '',
        myob_business_id: mapping?.myob_business_id || '',
        myob_contact_id: mapping?.myob_contact_id || '',
    };
}

function sameMyobMapping(left, right) {
    return left.myob_profile_id === right.myob_profile_id
        && left.myob_business_id === right.myob_business_id
        && left.myob_contact_id === right.myob_contact_id;
}

export default function UserDetails() {
    const [allowedRegions, setAllowedRegions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [alertVariant, setAlertVariant] = useState("info");
    const [agencies, setAgencies] = useState([]);
    const [accessDetail, setAccessDetail] = useState(null);
    const [properties, setProperties] = useState([]);
    const [propertySearch, setPropertySearch] = useState("");
    const [myobProfiles, setMyobProfiles] = useState([]);
    const [businessesAndAccounts, setBusinessesAndAccounts] = useState({});
    const [myobMapping, setMyobMapping] = useState(EMPTY_MYOB_MAPPING);
    const [initialMyobMapping, setInitialMyobMapping] = useState(EMPTY_MYOB_MAPPING);
    const [myobRemoveRequested, setMyobRemoveRequested] = useState(false);
    const [myobLoadError, setMyobLoadError] = useState(null);
    const [workerCatalogs, setWorkerCatalogs] = useState({skills: [], serviceAreas: []});
    const [catalogWarning, setCatalogWarning] = useState(null);
    let navigate = useNavigate();
    
    const [formDetail, setFormDetail] = useState({
        "username": "",
        "cos_customer_name": "",
        "agent_name": "",
        "role": "",
        "phone": "",
        "email": ""
    });
    
    const [responseMessage, setResponseMessage] = useState();
    // We can use the `useParams` hook here to access
    // the dynamic pieces of the URL.
    let {username} = useParams();

    useEffect(() => {
        let active = true;

        async function loadDetails() {
            setIsLoading(true);
            setMyobLoadError(null);
            const results = await Promise.allSettled([
                getAllowedRegion().then(response => requireSuccess(response, 'Error fetching allowed regions')).then(response => response.json()),
                getUserbyUsername(username).then(response => requireSuccess(response, 'Error fetching user details')).then(response => response.json()),
                getAgencies().then(response => requireSuccess(response, 'Error fetching Agencies')).then(response => response.json()),
                getAllProperties().then(response => requireSuccess(response, 'Error fetching properties')).then(response => response.json()),
                getProfileList().then(response => requireSuccess(response, 'Failed to load MYOB profiles')).then(response => response.json()),
                getBusinessesAndAccounts('false').then(response => requireSuccess(response, 'Failed to load MYOB contacts')).then(response => response.json()),
                getAgencyDefaults().then(response => requireSuccess(response, 'Failed to load Agency MYOB defaults')).then(response => response.json()),
                getWorkerProfileCatalogs(),
            ]);
            if (!active) return;

            const [regionsResult, userResult, agenciesResult, propertiesResult,
                profilesResult, businessesResult, defaultsResult, catalogsResult] = results;

            if (regionsResult.status === 'fulfilled') setAllowedRegions(regionsResult.value);
            if (agenciesResult.status === 'fulfilled') setAgencies(agenciesResult.value);
            if (propertiesResult.status === 'fulfilled') setProperties(propertiesResult.value);

            if (userResult.status === 'rejected') {
                setResponseMessage(userResult.reason.message || 'Error fetching user details');
                setAlertVariant('danger');
                setFormDetail(null);
                setIsLoading(false);
                return;
            }

            const user = userResult.value;
            setFormDetail({...user, worker_profile: normalizeWorkerProfile(user.worker_profile)});
            if (catalogsResult.status === 'fulfilled') {
                setWorkerCatalogs(catalogsResult.value);
                setCatalogWarning(null);
            } else {
                setWorkerCatalogs({skills: [], serviceAreas: []});
                setCatalogWarning(catalogsResult.reason?.message || 'Worker catalogs are unavailable');
            }
            if (user.role === 'PROPERTY_MANAGER') {
                try {
                    const accessResponse = await requireSuccess(
                        await getPropertyManagerAccess(username),
                        'Error fetching Property Manager access'
                    );
                    if (active) setAccessDetail(await accessResponse.json());
                } catch (error) {
                    if (active) {
                        setResponseMessage(error.message);
                        setAlertVariant('danger');
                    }
                }
            }

            const myobErrors = [];
            if (profilesResult.status === 'fulfilled') setMyobProfiles(profilesResult.value);
            else myobErrors.push(profilesResult.reason.message);
            if (businessesResult.status === 'fulfilled') setBusinessesAndAccounts(businessesResult.value);
            else myobErrors.push(businessesResult.reason.message);
            if (defaultsResult.status === 'fulfilled') {
                const current = normalizeMyobMapping(
                    defaultsResult.value.find(mapping => mapping.cos_customer_id === user.cos_customer_id)
                );
                setMyobMapping(current);
                setInitialMyobMapping(current);
                setMyobRemoveRequested(false);
            } else {
                myobErrors.push(defaultsResult.reason.message);
            }
            setMyobLoadError(myobErrors.length > 0 ? myobErrors.join('. ') : null);
            setIsLoading(false);
        }

        loadDetails().catch(error => {
            if (!active) return;
            setResponseMessage(error.message || 'An error occurred while fetching data');
            setAlertVariant('danger');
            setIsLoading(false);
        });

        return () => {
            active = false;
        };
    }, [username]);


    function handleFormChange(event) {
        setFormDetail({
            ...formDetail, [event.target.name]: event.target.value,
            ...(event.target.name === 'role' && event.target.value === 'WORKER' && !formDetail.worker_profile
                ? {worker_profile: createDefaultWorkerProfile()}
                : {})
        })
    }

    const myobBusinesses = useMemo(
        () => businessesAndAccounts[myobMapping.myob_profile_id] || [],
        [businessesAndAccounts, myobMapping.myob_profile_id]
    );
    const myobContacts = useMemo(
        () => myobBusinesses.find(business => business.uid === myobMapping.myob_business_id)?.contacts || [],
        [myobBusinesses, myobMapping.myob_business_id]
    );
    const myobMappingHasValue = Boolean(
        myobMapping.myob_profile_id || myobMapping.myob_business_id || myobMapping.myob_contact_id
    );
    const myobMappingComplete = Boolean(
        myobMapping.myob_profile_id && myobMapping.myob_business_id && myobMapping.myob_contact_id
    );
    const myobMappingDirty = formDetail?.role === 'AGENT'
        && !myobLoadError
        && !sameMyobMapping(myobMapping, initialMyobMapping);

    function changeMyobProfile(profileId) {
        setMyobMapping({
            myob_profile_id: profileId,
            myob_business_id: '',
            myob_contact_id: '',
        });
        setMyobRemoveRequested(false);
    }

    function changeMyobBusiness(businessId) {
        setMyobMapping(current => ({
            ...current,
            myob_business_id: businessId,
            myob_contact_id: '',
        }));
        setMyobRemoveRequested(false);
    }

    function removeMyobMapping() {
        setMyobMapping(EMPTY_MYOB_MAPPING);
        setMyobRemoveRequested(true);
    }

    async function handleFormSubmit(event) {
        event.preventDefault();
        setShowConfirmModal(false);
        setResponseMessage("");
        setIsLoading(true);

        const updateRequest = buildUserUpdateRequest(formDetail, accessDetail);
        const operations = [{
            key: 'user',
            promise: saveUserWithRegionConfirmation(updateRequest),
        }];

        if (myobMappingDirty) {
            operations.push({
                key: 'myob',
                promise: myobRemoveRequested
                    ? deleteAgencyDefault(formDetail.cos_customer_id)
                        .then(response => requireSuccess(response, 'MYOB Contact removal failed'))
                    : setAgencyDefault(formDetail.cos_customer_id, myobMapping)
                        .then(response => requireSuccess(response, 'MYOB Contact update failed')),
            });
        }

        const results = await Promise.allSettled(operations.map(operation => operation.promise));
        const resultByKey = new Map(operations.map((operation, index) => [operation.key, results[index]]));
        const userResult = resultByKey.get('user');
        const myobResult = resultByKey.get('myob');
        const userSucceeded = userResult.status === 'fulfilled';
        const myobSucceeded = !myobResult || myobResult.status === 'fulfilled';

        if (myobResult?.status === 'fulfilled') {
            const savedMapping = myobRemoveRequested ? EMPTY_MYOB_MAPPING : normalizeMyobMapping(myobMapping);
            setInitialMyobMapping(savedMapping);
            setMyobMapping(savedMapping);
            setMyobRemoveRequested(false);
        }

        if (userSucceeded && myobSucceeded) {
            setResponseMessage(myobResult
                ? 'User and MYOB Contact updated successfully!'
                : 'User updated successfully!');
            setAlertVariant('success');
            if (username !== formDetail.username) {
                navigate(`/manage-user/user-details/${formDetail.username}`, {replace: true});
            }
        } else if (userSucceeded) {
            setResponseMessage(`User updated, but MYOB Contact update failed: ${myobResult.reason.message}`);
            setAlertVariant('warning');
        } else if (myobSucceeded && myobResult) {
            setResponseMessage(`MYOB Contact updated, but user update failed: ${userResult.reason.message}`);
            setAlertVariant('warning');
        } else {
            const messages = [userResult.reason?.message, myobResult?.reason?.message].filter(Boolean);
            setResponseMessage(`Update failed: ${messages.join('. ')}`);
            setAlertVariant('danger');
        }
        setIsLoading(false);
    }

    async function saveUserWithRegionConfirmation(updateRequest) {
        let response = await changeUserDetail(username, updateRequest);
        if (response.status !== 409) {
            return requireSuccess(response, 'User update failed');
        }
        const conflict = await response.json().catch(() => null);
        const incompatible = conflict?.incompatible_service_areas || [];
        if (incompatible.length === 0) {
            throw new Error(conflict?.message || 'User update conflicted with existing data');
        }
        const areaNames = incompatible.map(area => `${area.name} ${area.postcode}`).join(', ');
        if (!window.confirm(`Changing Region will remove these Service Areas: ${areaNames}. Continue?`)) {
            throw new Error('Region change cancelled; no Worker Profile data was removed.');
        }
        const incompatibleIds = new Set(incompatible.map(area => area.id));
        const filteredProfile = {
            ...updateRequest.worker_profile,
            service_areas: (updateRequest.worker_profile?.service_areas || [])
                .filter(area => !incompatibleIds.has(area.service_area_id)),
        };
        const confirmedRequest = {
            ...updateRequest,
            worker_profile: filteredProfile,
            remove_incompatible_area_assignments: true,
        };
        setFormDetail(current => ({...current, worker_profile: filteredProfile}));
        response = await changeUserDetail(username, confirmedRequest);
        return requireSuccess(response, 'User update failed after confirming Region change');
    }
    
    function handleConfirmSubmit() {
        if (formDetail.role === 'AGENT' && myobMappingHasValue && !myobMappingComplete) {
            setResponseMessage('MYOB Profile, Business and Contact are all required.');
            setAlertVariant('danger');
            return;
        }
        setShowConfirmModal(true);
    }
    
    function goBack() {
        navigate('/manage-user');
    }

    function updateAccess(name, value) {
        setAccessDetail({...accessDetail, [name]: value});
    }

    function toggleAccessProperty(propertyId) {
        const selected = accessDetail.property_ids || [];
        updateAccess("property_ids", selected.includes(propertyId)
            ? selected.filter(id => id !== propertyId)
            : [...selected, propertyId]);
    }

    function changeAgency(agencyId) {
        if (accessDetail?.agency_cos_customer_id
            && accessDetail.agency_cos_customer_id !== agencyId
            && !window.confirm("Moving this Property Manager to another Agency will clear all existing property assignments. Continue?")) {
            return;
        }
        setAccessDetail({
            ...accessDetail,
            agency_cos_customer_id: agencyId,
            mode: "SELECTED",
            property_ids: []
        });
        setPropertySearch("");
    }

    const selectableProperties = properties.filter(property =>
        property.cos_customer_id === accessDetail?.agency_cos_customer_id
        && (property.full_address || "").toLowerCase().includes(propertySearch.toLowerCase()));

    return (
        <Container className="py-4">
            <Button 
                variant="outline-secondary" 
                className="mb-3" 
                onClick={goBack}
            >
                <FaArrowLeft className="me-2" /> Back to Users
            </Button>
            
            <Card className="shadow-sm">
                <Card.Header as="h5" className="bg-primary text-white">
                    <FaUser className="me-2" /> Update User: {username}
                </Card.Header>
                <Card.Body>
                    {responseMessage && (
                        <Alert variant={alertVariant} className="mb-4">
                            {responseMessage}
                        </Alert>
                    )}
                    
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading user details...</p>
                        </div>
                    ) : formDetail ? (
                        <Form onSubmit={handleFormSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="username">
                                        <Form.Label>Username</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaUser />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="username" 
                                                type="text" 
                                                placeholder="Username" 
                                                value={formDetail.username}
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="password">
                                        <Form.Label>Password</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaLock />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="password" 
                                                type={showPassword ? "text" : "password"} 
                                                placeholder="No Change"
                                                value={formDetail.password || ''}
                                                onChange={handleFormChange}
                                            />
                                            <Button 
                                                variant="outline-secondary"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </Button>
                                        </InputGroup>
                                        <Form.Text className="text-muted">
                                            Leave blank to keep current password
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row className="mt-2">
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="cos_customer_name">
                                        <Form.Label>Name</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaIdCard />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="cos_customer_name" 
                                                type="text" 
                                                placeholder="Customer name"
                                                value={formDetail.cos_customer_name}
                                                disabled
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="agent_name">
                                        <Form.Label>Agent Name</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaUserTag />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="agent_name" 
                                                type="text" 
                                                placeholder="Agent name"
                                                value={formDetail.agent_name}
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                        <Form.Text className="text-muted">
                                            Enter 'RETRIED' if you don't want a worker to show up in Scheduler
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row className="mt-2">
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="phone">
                                        <Form.Label>Phone</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaPhone />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="phone" 
                                                type="text" 
                                                placeholder="Phone number"
                                                value={formDetail.phone || ''}
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="email">
                                        <Form.Label>Email</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaEnvelope />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                name="email" 
                                                type="email" 
                                                placeholder="Email address"
                                                value={formDetail.email || ''}
                                                onChange={handleFormChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row className="mt-2">
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="role">
                                        <Form.Label>Role</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaUserCog />
                                            </InputGroup.Text>
                                            <Form.Select 
                                                name="role" 
                                                value={formDetail.role}
                                                disabled={formDetail.role === "PROPERTY_MANAGER"}
                                                onChange={handleFormChange}
                                            >
                                                {USER_ROLES
                                                    .filter(role => role !== "PROPERTY_MANAGER"
                                                        || formDetail.role === "PROPERTY_MANAGER")
                                                    .map(role => (
                                                    <option key={role}>{role}</option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="region">
                                        <Form.Label>Region</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaMapMarkerAlt />
                                            </InputGroup.Text>
                                            <Form.Select 
                                                name="region" 
                                                value={formDetail.region}
                                                onChange={handleFormChange}
                                            >
                                                {allowedRegions.map((region, index) => (
                                                    <option key={index}>{region}</option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {formDetail.role === "AGENT" ? (
                                <Card className="mt-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <Card.Title className="mb-0">MYOB Contact</Card.Title>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={removeMyobMapping}
                                                disabled={Boolean(myobLoadError) || !myobMappingHasValue}
                                            >
                                                <FaTrash className="me-1" /> Remove MYOB Mapping
                                            </Button>
                                        </div>
                                        {myobLoadError ? (
                                            <Alert variant="warning" className="mb-0">
                                                {myobLoadError}. User details can still be saved, but MYOB Contact is unavailable.
                                            </Alert>
                                        ) : (
                                            <>
                                                <Row>
                                                    <Col md={4}>
                                                        <Form.Group className="mb-3" controlId="myob_profile_id">
                                                            <Form.Label>MYOB Profile</Form.Label>
                                                            <Form.Select
                                                                value={myobMapping.myob_profile_id}
                                                                onChange={event => changeMyobProfile(event.target.value)}
                                                            >
                                                                <option value="">Select Profile</option>
                                                                {myobProfiles.map(profile => (
                                                                    <option key={profile.uid} value={profile.uid}>
                                                                        {profile.username}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Group className="mb-3" controlId="myob_business_id">
                                                            <Form.Label>MYOB Business</Form.Label>
                                                            <Form.Select
                                                                value={myobMapping.myob_business_id}
                                                                onChange={event => changeMyobBusiness(event.target.value)}
                                                                disabled={!myobMapping.myob_profile_id}
                                                            >
                                                                <option value="">Select Business</option>
                                                                {myobBusinesses.map(business => (
                                                                    <option key={business.uid} value={business.uid}>
                                                                        {business.name}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Group className="mb-3" controlId="myob_contact_id">
                                                            <Form.Label>MYOB Contact</Form.Label>
                                                            <Form.Select
                                                                value={myobMapping.myob_contact_id}
                                                                onChange={event => {
                                                                    setMyobMapping(current => ({
                                                                        ...current,
                                                                        myob_contact_id: event.target.value,
                                                                    }));
                                                                    setMyobRemoveRequested(false);
                                                                }}
                                                                disabled={!myobMapping.myob_business_id}
                                                            >
                                                                <option value="">Select Contact</option>
                                                                {myobContacts.map(contact => (
                                                                    <option key={contact.uid} value={contact.uid}>
                                                                        {contact.name}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                {myobRemoveRequested ? (
                                                    <Alert variant="warning" className="mb-0">
                                                        The Agency MYOB Mapping will be removed when you save changes.
                                                    </Alert>
                                                ) : null}
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            ) : null}

                            {formDetail.role === "WORKER" ? (
                                <WorkerProfileEditor
                                    value={formDetail.worker_profile}
                                    onChange={workerProfile => setFormDetail(current => ({
                                        ...current, worker_profile: workerProfile
                                    }))}
                                    region={formDetail.region}
                                    skills={workerCatalogs.skills || []}
                                    serviceAreas={workerCatalogs.serviceAreas || []}
                                    catalogWarning={catalogWarning}
                                />
                            ) : null}

                            {formDetail.role === "PROPERTY_MANAGER" && accessDetail && (
                                <Card className="mt-3">
                                    <Card.Body>
                                        <Card.Title>Agency property access</Card.Title>
                                        <Form.Group className="mb-3">
                                            <AgencyAutocomplete
                                                agencies={agencies}
                                                value={accessDetail.agency_cos_customer_id || ""}
                                                onChange={changeAgency}
                                                disableClearable
                                                helperText="Type the start of an Agency name or username. Moving Agency clears existing assignments and leaves the PM with zero selected properties."
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Visibility</Form.Label>
                                            <Form.Select value={accessDetail.mode || "ALL"}
                                                         onChange={event => updateAccess("mode", event.target.value)}>
                                                <option value="ALL">All Agency properties</option>
                                                <option value="SELECTED">Selected properties only</option>
                                            </Form.Select>
                                        </Form.Group>
                                        {accessDetail.mode === "SELECTED" && (
                                            <div style={{maxHeight: 260, overflowY: "auto"}}>
                                                <Form.Control className="mb-2"
                                                              type="search"
                                                              placeholder="Search by address"
                                                              value={propertySearch}
                                                              onChange={event => setPropertySearch(event.target.value)}/>
                                                {selectableProperties.map(property => (
                                                    <Form.Check key={property.property_id}
                                                                type="checkbox"
                                                                checked={(accessDetail.property_ids || []).includes(property.property_id)}
                                                                onChange={() => toggleAccessProperty(property.property_id)}
                                                                label={property.full_address}/>
                                                ))}
                                                {selectableProperties.length === 0
                                                    && <Alert variant="secondary">No properties match this Agency and address.</Alert>}
                                                <small>{(accessDetail.property_ids || []).length} properties selected</small>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}

                            <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
                                <Button 
                                    variant="secondary" 
                                    className="me-md-2" 
                                    onClick={goBack}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="primary" 
                                    onClick={handleConfirmSubmit}
                                    disabled={isLoading || (formDetail.role === 'AGENT'
                                        && myobMappingHasValue && !myobMappingComplete)}
                                >
                                    {isLoading ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave className="me-2" /> Save Changes
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    ) : (
                        <Alert variant="danger">
                            Unable to load user details. Please try again later.
                        </Alert>
                    )}
                </Card.Body>
            </Card>
            
            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Changes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to update this user's information
                    {myobMappingDirty ? ' and Agency MYOB Contact' : ''}?
                    {username !== formDetail.username && (
                        <Alert variant="warning" className="mt-3">
                            <strong>Warning:</strong> You are changing the username from "{username}" to "{formDetail.username}".
                            This will redirect you to the new user page.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleFormSubmit}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
